import React, { memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';

import Badge from '@common/components/badge/badge';
import Icon from '@common/components/icon';
import { UserStatusBadge } from '@common/components/profile-image';

import ConversationImage from '../conversation-image';
import ConversationActions from '../conversation-actions';
import ConversationArchivedActions from '../conversation-archived-actions';
import MessageLast from '../message-last';

import { useAppSelector } from '@common/hooks/redux';

import { createTimeStamp } from '@common/utils/create-timestamp';
import { combineClassNames } from '@common/utils/combineClassNames';

import { selected } from '@modules/core/selectors/logged-user';

import { ESearhFilterTypes, FullConversation, PrivateMessage } from '@modules/chat/types/objects';
import { RouteParams } from '@modules/chat/definitions';
import { User, UserStatus } from '@common/types/objects';
import { ConversationFormPayload } from '@modules/chat/forms/conversation/conversation';
import { EStatus } from '@common/definitions';
import ConversationStatus from '../conversation-status';
import { useChatStore } from '@modules/chat/store/chatStore';

interface ConversationItemProps {
  currentConversationId: string;
  item: FullConversation;
  basePath: string;
  usersStatus: Record<string, UserStatus | null>;
  onLeave: (id: string) => void;
  onOpenProfile: (userId: string) => void;
  onToggleArchive: (id: string, shouldArchive: boolean) => void;
  onToggleNotifications: (conversationId: string, is_muted: boolean) => void;
  onEditGroupAdmin: (conversationId: string, user: User, add: boolean) => void;
  onRemoveConversation: (conversationId: string) => void;
  onUpdateConversation: (id: string, data: ConversationFormPayload) => void;
  onEditParticipants: (
    conversationId: string, activities: PrivateMessage | PrivateMessage[], participants: User | User[],
  ) => void;
  onConversationPinOrUnpin: (conversationId: string, isPinned: boolean) => void;
  onConversationMarkAsReadOrUnread: (conversationId: string, isRead: boolean) => void;
  onConversationFavouriteOrUnfavourite: (conversationId: string, isFavourite: boolean) => void;
  onOpenConversation: (conversation: FullConversation) => void;
  index?: number;
  selectedIndex?: number;
  onClick?: () => void;
}

const ConversationItem: React.FC<ConversationItemProps> = memo(({
  currentConversationId, item, basePath, usersStatus,
  onOpenProfile, onToggleArchive, onToggleNotifications,
  onRemoveConversation, onEditGroupAdmin, onEditParticipants, onLeave, onUpdateConversation,
  onConversationPinOrUnpin, onConversationMarkAsReadOrUnread, onConversationFavouriteOrUnfavourite,
  onOpenConversation, index, selectedIndex, onClick
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { filter } = useParams<RouteParams>();
  const loggedUser = useAppSelector(selected);
  const { searchFilter, setOpenChatFilesSidebar } = useChatStore();

  const handleClick = useCallback(() => {
    const searchParams = history.location.search;
    if (searchFilter === ESearhFilterTypes.MESSAGES) {
      const messageId = item.last_message?.id;
      if (messageId) {
        history.push(filter ?
          `${basePath}/filter/${filter}/${item.id}/message/${messageId}${searchParams}` :
          `${basePath}/${item.id}/message/${messageId}${searchParams}`);
      } else {
        history.push(filter ?
          `${basePath}/filter/${filter}/${item.id}${searchParams}` :
          `${basePath}/${item.id}${searchParams}`);
      }
    } else {
      history.push(filter ?
        `${basePath}/filter/${filter}/${item.id}${searchParams}` :
        `${basePath}/${item.id}${searchParams}`);
    }
    onOpenConversation(item);
  }, [item.id, history, filter, basePath, searchFilter, item.last_message?.id, onOpenConversation]);

  const status = item.participant?.id ? usersStatus[item.participant?.id] : null;

  const isActive = searchFilter === ESearhFilterTypes.MESSAGES
    ? index === selectedIndex
    : currentConversationId === item.id;

  return (
    <div
      className={combineClassNames('ConversationItem fs-exclude ImageItem ImageItem--large', {
        'ConversationItem--state-active': isActive,
      })}
      role="button"
      onClick={() => {
        setOpenChatFilesSidebar(false);
        if (onClick) onClick();
        handleClick();
      }}
    >
      {status && (
        <UserStatusBadge
          formattedStatus={status}
          profileImageSize={40}
        />
      )}
      <ConversationImage
        loggedUser={loggedUser}
        conversation={item}
        className={status ? 'hasStatus' : undefined}
      />
      <div className="tw-flex tw-flex-col tw-flex-1 tw-min-w-0">
        <h5 className="tw-flex tw-items-center tw-leading-6 tw-text-base tw-normal-case tw-m-0">
          <div className="tw-flex tw-items-center tw-flex-1 tw-overflow-hidden tw-text-ellipsis tw-whitespace-nowrap">
            {item.name || item.participant?.full_name}
            {item.is_muted && (
              <Icon
                type="notifications_off"
                color="grey"
                className="!tw-float-none !tw-w-4 !tw-h-4 tw-ml-2"
              />
            )}
          </div>
          <div className="tw-flex tw-items-center tw-gap-2">
            <small>
              {createTimeStamp(item.last_message?.created_at || item.created_at)}
            </small>
          </div>
        </h5>
        <div className="ConversationItem__Content">
          <MessageLast
            type={item.conversation_type}
            message={item.last_message}
          />
          {item.is_archived ? (
            <div className="ConversationItem__Archived">
              <Badge
                status={EStatus.ARCHIVED}
                text={t('chat:conversation_archived')}
              />
              <ConversationArchivedActions
                conversation={item}
                onUnarchive={() => onToggleArchive(item.id, false)}
                onRemoveConversation={onRemoveConversation}
              />
            </div>
          ) : ((
            <div className="tw-flex tw-items-center tw-gap-1">
              <ConversationStatus conversation={item} />
              <ConversationActions
                conversation={item}
                usersStatus={usersStatus}
                onOpenProfile={onOpenProfile}
                onToggleNotifications={onToggleNotifications}
                onArchive={() => onToggleArchive(item.id, true)}
                onEditGroupAdmin={onEditGroupAdmin}
                onEditParticipants={onEditParticipants}
                onRemoveConversation={onRemoveConversation}
                onLeave={onLeave}
                onUpdateConversation={onUpdateConversation}
                onConversationPinOrUnpin={onConversationPinOrUnpin}
                onConversationMarkAsReadOrUnread={onConversationMarkAsReadOrUnread}
                onConversationFavouriteOrUnfavourite={onConversationFavouriteOrUnfavourite}
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
});

export default ConversationItem;
